import React, { useEffect, useMemo } from "react";
import { Box, Typography, Button, TextField, useTheme } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ProductItem,
  EditProductItemFormData,
  EditProductItemModalProps
} from "./types/products";
import { useUpdateProductItem } from "../../hooks/api/useProductItem";
import {
  shortLabelYupTest,
  getPurchasePricePlaceholder,
  getSalesPricePlaceholder,
  getMinSalesPricePlaceholder,
  getUnitLabel
} from "../../Utils/productValidation";
import {
  optionalNonNegativeNumber,
  requiredNonNegativeNumber
} from "./utils/productItemSchemaHelpers";
import FormModal, {
  formFieldSx,
  formSectionTitleSx
} from "../common/FormModal/FormModal";
import { unitFromItemBase, buildDefaultValues } from "./utils/productItemUtils";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

const EditProductItemModal: React.FC<EditProductItemModalProps> = ({
  open,
  onClose,
  item,
  onSaved
}) => {
  const { t } = useKivunovaTranslation();
  const updateProductItem = useUpdateProductItem();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  // ── Schema ────────────────────────────────────────────────────────────────
  const validationSchema = useMemo(() => {
    const tFn = t;
    return Yup.object({
      variationName: Yup.string()
        .trim()
        .required(
          tFn(
            "edit-product-item-variation-required",
            "Variation name is required"
          )
        )
        .max(
          150,
          tFn(
            "edit-product-item-variation-max",
            "Variation name must be at most 150 characters"
          )
        )
        .test(
          shortLabelYupTest(
            tFn("edit-product-item-variation-label", "Variation name"),
            150
          )
        ),

      lotNumber: Yup.string()
        .max(
          100,
          tFn(
            "edit-product-item-lot-max",
            "Lot number must be at most 100 characters"
          )
        )
        .required(
          tFn("edit-product-item-lot-required", "Lot number is required")
        ),

      purchasePrice: requiredNonNegativeNumber(
        tFn,
        "edit-product-item-purchase-price-required",
        "Purchase price is required",
        "edit-product-item-purchase-price-min",
        "Purchase price must be 0 or greater"
      ),

      salesPrice: requiredNonNegativeNumber(
        tFn,
        "edit-product-item-sales-price-required",
        "Sales price is required",
        "edit-product-item-sales-price-min",
        "Sales price must be 0 or greater"
      ),

      minSalesPrice: requiredNonNegativeNumber(
        tFn,
        "edit-product-item-min-sales-price-required",
        "Min sales price is required",
        "edit-product-item-min-sales-price-min",
        "Min sales price must be 0 or greater"
      ),

      weight: optionalNonNegativeNumber(
        tFn,
        "edit-product-item-weight-min",
        "Weight"
      ),
      height: optionalNonNegativeNumber(
        tFn,
        "edit-product-item-height-min",
        "Height"
      ),
      width: optionalNonNegativeNumber(
        tFn,
        "edit-product-item-width-min",
        "Width"
      ),
      thickness: optionalNonNegativeNumber(
        tFn,
        "edit-product-item-thickness-min",
        "Thickness"
      ),

      color: Yup.string()
        .max(
          100,
          tFn(
            "edit-product-item-color-max",
            "Color must be at most 100 characters"
          )
        )
        .optional(),

      material: Yup.string()
        .max(
          100,
          tFn(
            "edit-product-item-material-max",
            "Material must be at most 100 characters"
          )
        )
        .optional(),

      packagingType: Yup.string()
        .max(
          100,
          tFn(
            "edit-product-item-packaging-type-max",
            "Packaging type must be at most 100 characters"
          )
        )
        .optional(),

      packSize: Yup.string()
        .max(
          50,
          tFn(
            "edit-product-item-pack-size-max",
            "Pack size must be at most 50 characters"
          )
        )
        .optional()
    }) as Yup.ObjectSchema<EditProductItemFormData>;
  }, [t]);

  // ── Form ──────────────────────────────────────────────────────────────────
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<EditProductItemFormData>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: buildDefaultValues(item)
  });

  useEffect(() => {
    if (open) {
      reset(buildDefaultValues(item));
    }
  }, [open, item, reset]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const onFormSubmit: SubmitHandler<EditProductItemFormData> = async (data) => {
    // Log start of update
    sendAppLog(
      LOG_LEVEL.INFO,
      "Started updating product item",
      buildLogContext(
        "product-items",
        "update-product-item-ui",
        userDetails?.id,
        userDetails?.companyId,
        userDetails?.storeId,
        {
          productItemId: item.id,
          productId: item.productId,
          variationName: data.variationName
        }
      )
    );

    try {
      const updated: ProductItem = {
        ...item,
        variationName: data.variationName.trim(),
        lotNumber: data.lotNumber?.trim() || "",
        purchasePrice: data.purchasePrice!,
        salesPrice: data.salesPrice!,
        price: data.salesPrice!,
        minSalesPrice: data.minSalesPrice!,
        color: data.color?.trim() || "",
        material: data.material?.trim() || "",
        packagingType: data.packagingType?.trim() || "",
        packSize: data.packSize?.trim() || "",
        ...(data.weight !== undefined && { weight: data.weight }),
        ...(data.height !== undefined && { height: data.height }),
        ...(data.width !== undefined && { width: data.width }),
        ...(data.thickness !== undefined && { thickness: data.thickness })
      };

      const id = await updateProductItem.mutateAsync({ productItem: updated });
      if (id) {
        // Success is already logged inside the hook
        onSaved();
        onClose();
      }
    } catch (err) {
      captureAppException(
        "Failed to update product item",
        err,
        buildLogContext(
          "product-items",
          "update-product-item-ui",
          userDetails?.id,
          userDetails?.companyId,
          userDetails?.storeId,
          { productItemId: item.id, productId: item.productId }
        )
      );
    }
  };

  const isLoading = isSubmitting || updateProductItem.isPending;

  // ── Placeholder generation ────────────────────────────────────────────────
  const effectiveUnit = unitFromItemBase(item);
  const unitDisplayLabel = getUnitLabel(effectiveUnit, t);
  const purchasePricePlaceholder = getPurchasePricePlaceholder(
    effectiveUnit,
    t
  );
  const salesPricePlaceholder = getSalesPricePlaceholder(effectiveUnit, t);
  const minSalesPricePlaceholder = getMinSalesPricePlaceholder(
    effectiveUnit,
    t
  );

  // ── Render helpers ────────────────────────────────────────────────────────

  const renderTextField = (
    name: keyof EditProductItemFormData,
    label: string,
    type: "text" | "number" = "text",
    placeholder?: string,
    extraInputProps?: object
  ) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          value={field.value ?? ""}
          fullWidth
          size={matchMD ? "small" : "medium"}
          label={label}
          type={type}
          placeholder={placeholder}
          disabled={isLoading}
          error={!!errors[name]}
          helperText={errors[name]?.message}
          inputProps={
            type === "number"
              ? { min: 0, step: 0.01, ...extraInputProps }
              : extraInputProps
          }
          sx={formFieldSx}
        />
      )}
    />
  );

  // ── JSX ───────────────────────────────────────────────────────────────────

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={t("edit-product-item-title", "Edit Product Item")}
      dataTestId="edit-product-item-modal"
      icon={<EditRoundedIcon sx={{ color: BRAND_COLOR, fontSize: 28 }} />}
      footer={
        <>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            size={matchMD ? "medium" : "large"}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                borderColor: "text.secondary",
                backgroundColor: "action.hover"
              }
            }}
          >
            {t("cancel", "Cancel")}
          </Button>
          <LoadingButton
            form="edit-product-item-form"
            type="submit"
            variant="contained"
            loading={isLoading}
            size={matchMD ? "medium" : "large"}
            data-testid="edit-product-item-submit"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              backgroundColor: BRAND_COLOR,
              boxShadow: "none",
              "&:hover": { backgroundColor: BRAND_HOVER, boxShadow: "none" }
            }}
          >
            {t("save", "Save")}
          </LoadingButton>
        </>
      }
    >
      <Box
        component="form"
        id="edit-product-item-form"
        onSubmit={handleSubmit(onFormSubmit)}
        noValidate
        sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
      >
        <Typography variant="body2" color="text.secondary">
          {t(
            "edit-product-item-hint",
            "You can update the variation name, lot, prices, and physical specifications."
          )}
        </Typography>

        {/* ── Identifiers ───────────────────────────────────────────────── */}
        <Typography component="span" sx={formSectionTitleSx}>
          {t("edit-product-item-section-identifiers", "Identifiers")}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2
          }}
        >
          {renderTextField(
            "variationName",
            t("edit-product-item-variation", "Variation Name*"),
            "text",
            t(
              "edit-product-item-variation-placeholder",
              "e.g. 500ml bottle, Red size M"
            ),
            { maxLength: 150 }
          )}
          {renderTextField(
            "lotNumber",
            t("edit-product-item-lot-number", "Lot Number"),
            "text",
            t("edit-product-item-lot-placeholder", "e.g. LOT-2024-001"),
            { maxLength: 100 }
          )}
        </Box>

        {/* ── Pricing ───────────────────────────────────────────────────── */}
        <Typography component="span" sx={{ ...formSectionTitleSx, mt: 3 }}>
          {t("edit-product-item-section-pricing", "Pricing")}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2
          }}
        >
          {renderTextField(
            "purchasePrice",
            t(
              "edit-product-item-purchase-price",
              effectiveUnit
                ? `Purchase Price* (per ${unitDisplayLabel})`
                : "Purchase Price*"
            ),
            "number",
            purchasePricePlaceholder
          )}
          {renderTextField(
            "salesPrice",
            t(
              "edit-product-item-sales-price",
              effectiveUnit
                ? `Sales Price* (per ${unitDisplayLabel})`
                : "Sales Price*"
            ),
            "number",
            salesPricePlaceholder
          )}
          {renderTextField(
            "minSalesPrice",
            t(
              "edit-product-item-min-sales-price",
              effectiveUnit
                ? `Min Sales Price* (per ${unitDisplayLabel})`
                : "Min Sales Price*"
            ),
            "number",
            minSalesPricePlaceholder
          )}
        </Box>

        {/* ── Physical specifications ───────────────────────────────────── */}
        <Typography component="span" sx={{ ...formSectionTitleSx, mt: 3 }}>
          {t("edit-product-item-section-physical", "Physical Specifications")}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2
          }}
        >
          {renderTextField(
            "weight",
            t("edit-product-item-weight", "Weight"),
            "number",
            t("edit-product-item-weight-placeholder", "e.g. 0.5 (kg)")
          )}
          {renderTextField(
            "height",
            t("edit-product-item-height", "Height"),
            "number",
            t("edit-product-item-height-placeholder", "e.g. 20 (cm)")
          )}
          {renderTextField(
            "width",
            t("edit-product-item-width", "Width"),
            "number",
            t("edit-product-item-width-placeholder", "e.g. 10 (cm)")
          )}
          {renderTextField(
            "thickness",
            t("edit-product-item-thickness", "Thickness"),
            "number",
            t("edit-product-item-thickness-placeholder", "e.g. 2 (cm)")
          )}
          {renderTextField(
            "color",
            t("edit-product-item-color", "Color"),
            "text",
            t("edit-product-item-color-placeholder", "e.g. Red, Blue"),
            { maxLength: 100 }
          )}
          {renderTextField(
            "material",
            t("edit-product-item-material", "Material"),
            "text",
            t("edit-product-item-material-placeholder", "e.g. Cotton, Plastic"),
            { maxLength: 100 }
          )}
          {renderTextField(
            "packagingType",
            t("edit-product-item-packaging-type", "Packaging Type"),
            "text",
            t(
              "edit-product-item-packaging-placeholder",
              "e.g. Box, Bag, Blister"
            ),
            { maxLength: 100 }
          )}
          {renderTextField(
            "packSize",
            t("edit-product-item-pack-size", "Pack Size"),
            "text",
            t(
              "edit-product-item-pack-size-placeholder",
              "e.g. 12-pack, 500g bag"
            ),
            { maxLength: 50 }
          )}
        </Box>
      </Box>
    </FormModal>
  );
};

export default EditProductItemModal;
